import React, { useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';

interface GoogleSignInProps {
  onSuccess: (credential: string) => void;
  onError: (error: string) => void;
  disabled?: boolean;
}

declare global {
  interface Window {
    google: any;
  }
}

const GoogleSignIn: React.FC<GoogleSignInProps> = ({ onSuccess, onError, disabled = false }) => {
  const googleButtonRef = useRef<HTMLDivElement>(null);

  const handleCredentialResponse = useCallback((response: any) => {
    if (response.credential) {
      // Track Google Sign-In success
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'sign_in', {
          event_category: 'authentication',
          event_label: 'google',
          method: 'google_sign_in',
        });
      }
      onSuccess(response.credential);
    } else {
      // Track Google Sign-In error
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'sign_in_error', {
          event_category: 'authentication',
          event_label: 'google',
          method: 'google_sign_in',
        });
      }
      onError('Failed to get Google credential');
    }
  }, [onSuccess, onError]);

  const initializeGoogleSignIn = useCallback(() => {
    if (!window.google || !googleButtonRef.current) return;

    const clientId = '879353561530-c7kaqpdmj01nj95pbkbjuv2onc6s5jfk.apps.googleusercontent.com'//process.env.REACT_APP_GOOGLE_CLIENT_ID;
    
    if (!clientId) {
      // Show a placeholder button when no client ID is configured
      if (googleButtonRef.current) {
        googleButtonRef.current.innerHTML = `
          <div style="
            width: 100%;
            height: 40px;
            border: 1px solid #dadce0;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #fff;
            color: #3c4043;
            font-family: 'Google Sans', Roboto, sans-serif;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
          " onclick="alert('Please configure REACT_APP_GOOGLE_CLIENT_ID in .env.local file')">
            <svg width="18" height="18" viewBox="0 0 24 24" style="margin-right: 8px;">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google (Configure Client ID)
          </div>
        `;
      }
      return;
    }

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleCredentialResponse,
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    window.google.accounts.id.renderButton(
      googleButtonRef.current,
      {
        theme: 'outline',
        size: 'large',
        width: '100%',
        text: 'signin_with',
        shape: 'rectangular',
      }
    );
  }, [handleCredentialResponse]);

  useEffect(() => {
    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogleSignIn;
    document.head.appendChild(script);

    return () => {
      // Cleanup script when component unmounts
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, [initializeGoogleSignIn]);

  return (
    <Box sx={{ width: '100%' }}>
      <div ref={googleButtonRef} style={{ width: '100%' }} />
    </Box>
  );
};

export default GoogleSignIn;
